import { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { InputTextarea, ModalForm, Notice, Text } from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../hooks/useService";
import { escapeHtml, formatBRL, formatCompetencia } from "../ow";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || "20px"};
`;

/**
 * Recusa de uma nota fiscal com motivo: na aba Pagamentos do contrato, no detalhe
 * do pagamento e na lista de notas fiscais. O back só exige o motivo não vazio; o prestador vê o texto.
 * Vale para nota pendente e para nota aprovada com o pagamento em aberto (o back
 * barra a nota paga e o cronograma já pago).
 *
 * Props:
 * - invoice: nota como vem da API
 * - schedule: cronograma da nota, ou só { reference_month, reference_year } (usado
 *   só para a competência no título)
 * - onClose
 * - onRejected: depois de recusar (recarregar)
 */
export default function RejectInvoiceModal({ invoice, schedule, onClose, onRejected }) {
  const taxInvoicesService = useService("taxInvoices");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  function close() {
    if (!loading) onClose();
  }

  async function confirm() {
    if (!reason.trim()) {
      setError("Informe o motivo da recusa");
      return;
    }
    setLoading(true);
    try {
      const result = await taxInvoicesService.reject(invoice.id, reason);
      if (result && !result.error) {
        toast.success("Nota fiscal rejeitada");
        setLoading(false);
        onRejected();
        return;
      }
      // Erro com { error: true } vem do sendError do service, que já mostrou o toast
      if (!result?.error) toast.error("Erro ao rejeitar NF");
    } catch (err) {
      toast.error("Erro ao rejeitar nota fiscal");
    }
    setLoading(false);
  }

  const competence = { month: Number(schedule.reference_month), year: Number(schedule.reference_year) };

  return (
    <ModalForm
      $showModal
      $setShowModal={close}
      $noClose={loading}
      $isLoading={loading}
      $size="medium"
      $title={`Recusar nota de ${formatCompetencia(competence, true)}`}
      $contentBackground="N0"
      $actions={[
        { text: "Cancelar", action: close, variant: "outline", isDisabled: loading },
        { text: "Recusar nota", action: confirm, variant: "danger", isDisabled: loading },
      ]}>
      <Stack>
        {invoice.status === "APPROVED" ? (
          <Notice
            $variant="orange"
            $text="Esta nota já foi aprovada. Ao recusar, o prestador precisa enviar uma nova nota dentro do prazo do contrato, e o valor do cronograma é recalculado se o contrato for por hora."
          />
        ) : null}
        <Text
          $text="%s · <strong>%s</strong>"
          $replaces={[escapeHtml(invoice.filename || "Nota fiscal"), formatBRL(invoice.value)]}
          $variant="paragraph_small_medium"
          $color="N700"
          $colorEmphasis="N900"
        />
        <Stack $gap="6px">
          <InputTextarea
            $idForm="reject-invoice-reason"
            $label="Motivo da recusa"
            $variant="default"
            $isRequired
            $errors={error}
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError("");
            }}
            placeholder="Ex.: valor da nota diferente do cronograma…"
          />
          <Text $text="O prestador vê este motivo na nota recusada." $variant="caption_medium" $color="N400" />
        </Stack>
      </Stack>
    </ModalForm>
  );
}
